import React, { useState, useEffect, useRef } from 'react';
import { 
  TrendingUp, TrendingDown, Filter, AlertCircle, 
  Calendar, DollarSign, Activity, Zap, RefreshCw, Loader,
  ChevronDown, ChevronUp, Info, BarChart3, Search, ChevronLeft, ChevronRight,
  BarChart2, HelpCircle, BookOpen, X, Lightbulb
} from 'lucide-react';
import MobileTable from './MobileTable';

// Tooltip Component for column headers
const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);
  
  return (
    <div 
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      <HelpCircle size={12} style={{ opacity: 0.6, cursor: 'help' }} />
      {show && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '8px',
          padding: '10px 14px',
          background: '#1e293b',
          border: '1px solid #475569',
          borderRadius: '8px',
          color: '#e2e8f0',
          fontSize: '12px',
          lineHeight: '1.5',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          maxWidth: '280px',
          whiteSpace: 'normal'
        }}>
          {content}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid #475569'
          }} />
        </div>
      )}
    </div>
  );
};

// Educational definitions for tooltips
const COLUMN_TOOLTIPS = {
  strike: "The price at which you can buy (call) or sell (put) the underlying stock if you exercise the option.",
  premium: "The price you pay to buy the option contract. This is your maximum loss for buying options. Multiply by 100 for total cost per contract.",
  bid: "The highest price buyers are currently willing to pay. You'd receive this price when selling.",
  ask: "The lowest price sellers are currently asking. You'd pay this price when buying.",
  change: "How much the option price has changed today compared to yesterday's close.",
  volume: "Number of contracts traded today. High volume = more liquidity and tighter spreads.",
  oi: "Open Interest - total number of outstanding contracts. High OI indicates active trading interest.",
  iv: "Implied Volatility - market's forecast of likely price movement. Higher IV = more expensive options.",
  delta: "How much the option price moves for every $1 move in the stock. Calls: 0 to 1, Puts: -1 to 0.",
  gamma: "Rate of change in delta. High gamma means delta changes quickly as stock moves.",
  theta: "Time decay - how much value the option loses each day. Usually negative (you lose money daily).",
  vega: "Sensitivity to volatility. How much option price changes for 1% change in IV."
};

// Learn Options Panel Component
const LearnOptionsPanel = ({ isOpen, onClose }) => {
  const [activeSection, setActiveSection] = useState('basics');
  
  if (!isOpen) return null;
  
  const sections = {
    basics: {
      title: '📚 Options Basics',
      content: (
        <div>
          <h4 style={{ color: '#10b981', marginBottom: '12px' }}>What are Options?</h4>
          <p style={{ marginBottom: '12px', lineHeight: '1.6' }}>
            Options are contracts that give you the <strong>right</strong> (but not obligation) to buy or sell a stock at a specific price before a certain date.
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
              <strong style={{ color: '#10b981' }}>📈 CALL Options</strong>
              <p style={{ fontSize: '13px', marginTop: '8px' }}>Right to <strong>BUY</strong> stock at strike price. You profit when stock goes UP.</p>
            </div>
            <div style={{ background: 'rgba(239, 68, 68, 0.15)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <strong style={{ color: '#ef4444' }}>📉 PUT Options</strong>
              <p style={{ fontSize: '13px', marginTop: '8px' }}>Right to <strong>SELL</strong> stock at strike price. You profit when stock goes DOWN.</p>
            </div>
          </div>
          
          <h4 style={{ color: '#60a5fa', marginBottom: '12px' }}>Key Terms</h4>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
            <li><strong>Strike Price:</strong> The price you can buy/sell at</li>
            <li><strong>Premium:</strong> What you pay for the option (shown in "Last" column)</li>
            <li><strong>Expiration:</strong> When the option expires (worthless if not exercised)</li>
            <li><strong>Contract:</strong> Controls 100 shares (Premium × 100 = Total Cost)</li>
          </ul>
        </div>
      )
    },
    reading: {
      title: '📊 Reading the Chain',
      content: (
        <div>
          <h4 style={{ color: '#fbbf24', marginBottom: '12px' }}>Understanding the Options Chain</h4>
          
          <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
            <strong>💡 Quick Tip:</strong> Click any option row to see its P&L projection chart!
          </div>
          
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <td style={{ padding: '8px', color: '#94a3b8' }}>Strike</td>
                <td style={{ padding: '8px' }}>Price at which you can exercise</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <td style={{ padding: '8px', color: '#fbbf24' }}>Last (Premium)</td>
                <td style={{ padding: '8px' }}><strong>This is the price!</strong> × 100 = cost per contract</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <td style={{ padding: '8px', color: '#94a3b8' }}>Bid/Ask</td>
                <td style={{ padding: '8px' }}>Current market prices to sell/buy</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <td style={{ padding: '8px', color: '#94a3b8' }}>Volume</td>
                <td style={{ padding: '8px' }}>Contracts traded today (liquidity indicator)</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #334155' }}>
                <td style={{ padding: '8px', color: '#94a3b8' }}>OI</td>
                <td style={{ padding: '8px' }}>Open Interest - total outstanding contracts</td>
              </tr>
              <tr>
                <td style={{ padding: '8px', color: '#94a3b8' }}>IV</td>
                <td style={{ padding: '8px' }}>Implied Volatility - higher = more expensive</td>
              </tr>
            </tbody>
          </table>
          
          <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px' }}>
            <strong style={{ color: '#10b981' }}>🎯 ITM vs OTM:</strong>
            <p style={{ fontSize: '13px', marginTop: '8px' }}>
              <strong>In-The-Money (ITM):</strong> Highlighted rows - option has intrinsic value<br/>
              <strong>Out-of-The-Money (OTM):</strong> Regular rows - option only has time value
            </p>
          </div>
        </div>
      )
    },
    greeks: {
      title: '🔢 The Greeks',
      content: (
        <div>
          <h4 style={{ color: '#8b5cf6', marginBottom: '12px' }}>Understanding Option Greeks</h4>
          <p style={{ marginBottom: '16px', color: '#94a3b8' }}>Greeks measure how option prices change with different factors:</p>
          
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ background: 'rgba(96, 165, 250, 0.15)', padding: '12px', borderRadius: '8px' }}>
              <strong style={{ color: '#60a5fa' }}>Δ Delta (-1 to 1)</strong>
              <p style={{ fontSize: '13px', marginTop: '4px' }}>
                Price change per $1 stock move. Delta of 0.50 = option gains $0.50 when stock rises $1.
                <br/><em style={{ color: '#94a3b8' }}>Also approximates probability of expiring ITM.</em>
              </p>
            </div>
            
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', padding: '12px', borderRadius: '8px' }}>
              <strong style={{ color: '#10b981' }}>Γ Gamma</strong>
              <p style={{ fontSize: '13px', marginTop: '4px' }}>
                How fast delta changes. High gamma = delta moves quickly. ATM options have highest gamma.
              </p>
            </div>
            
            <div style={{ background: 'rgba(239, 68, 68, 0.15)', padding: '12px', borderRadius: '8px' }}>
              <strong style={{ color: '#ef4444' }}>Θ Theta (Time Decay)</strong>
              <p style={{ fontSize: '13px', marginTop: '4px' }}>
                Daily value loss. Theta of -0.05 = option loses $5/day per contract. <strong>Works against buyers!</strong>
              </p>
            </div>
            
            <div style={{ background: 'rgba(139, 92, 246, 0.15)', padding: '12px', borderRadius: '8px' }}>
              <strong style={{ color: '#8b5cf6' }}>ν Vega</strong>
              <p style={{ fontSize: '13px', marginTop: '4px' }}>
                Sensitivity to volatility. Vega of 0.10 = option gains $10 per contract for each 1% IV increase.
              </p>
            </div>
          </div>
        </div>
      )
    },
    strategies: {
      title: '🎯 Quick Strategies',
      content: (
        <div>
          <h4 style={{ color: '#fbbf24', marginBottom: '12px' }}>Common Option Strategies</h4>
          
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ background: 'rgba(16, 185, 129, 0.15)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
              <strong style={{ color: '#10b981' }}>📈 Bullish (Stock Going Up)</strong>
              <ul style={{ fontSize: '13px', marginTop: '8px', paddingLeft: '16px' }}>
                <li><strong>Buy Call:</strong> Unlimited upside, risk = premium paid</li>
                <li><strong>Sell Put:</strong> Collect premium, risk = stock assignment</li>
              </ul>
            </div>
            
            <div style={{ background: 'rgba(239, 68, 68, 0.15)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <strong style={{ color: '#ef4444' }}>📉 Bearish (Stock Going Down)</strong>
              <ul style={{ fontSize: '13px', marginTop: '8px', paddingLeft: '16px' }}>
                <li><strong>Buy Put:</strong> Profit from decline, risk = premium paid</li>
                <li><strong>Sell Call:</strong> Collect premium (covered calls)</li>
              </ul>
            </div>
            
            <div style={{ background: 'rgba(139, 92, 246, 0.15)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
              <strong style={{ color: '#8b5cf6' }}>⚖️ Neutral/Volatility</strong>
              <ul style={{ fontSize: '13px', marginTop: '8px', paddingLeft: '16px' }}>
                <li><strong>Straddle:</strong> Buy call + put at same strike (expect big move)</li>
                <li><strong>Iron Condor:</strong> Profit from low volatility</li>
              </ul>
            </div>
          </div>
          
          <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(251, 191, 36, 0.15)', borderRadius: '8px', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
            <strong style={{ color: '#fbbf24' }}>⚠️ Risk Warning</strong>
            <p style={{ fontSize: '13px', marginTop: '8px' }}>
              Options can expire worthless. Never risk more than you can afford to lose. Start with paper trading!
            </p>
          </div>
        </div>
      )
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: '420px',
      maxWidth: '100vw',
      height: '100vh',
      background: '#0f172a',
      borderLeft: '1px solid #334155',
      zIndex: 1001,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '-4px 0 30px rgba(0,0,0,0.5)'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #334155',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'rgba(30, 41, 59, 0.5)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <BookOpen size={22} color="#8b5cf6" />
          <span style={{ fontSize: '18px', fontWeight: '700' }}>Learn Options</span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: '4px'
          }}
        >
          <X size={22} />
        </button>
      </div>
      
      {/* Section Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #334155',
        overflow: 'auto'
      }}>
        {Object.entries(sections).map(([key, section]) => (
          <button
            key={key}
            onClick={() => setActiveSection(key)}
            style={{
              flex: 1,
              padding: '12px 8px',
              background: activeSection === key ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
              border: 'none',
              borderBottom: activeSection === key ? '2px solid #8b5cf6' : '2px solid transparent',
              color: activeSection === key ? '#a78bfa' : '#64748b',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            {section.title.split(' ')[0]}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '20px'
      }}>
        <h3 style={{ marginBottom: '16px', color: '#e2e8f0' }}>
          {sections[activeSection].title}
        </h3>
        <div style={{ color: '#cbd5e1', fontSize: '14px' }}>
          {sections[activeSection].content}
        </div>
      </div>
      
      {/* Footer */}
      <div style={{
        padding: '16px 20px',
        borderTop: '1px solid #334155',
        background: 'rgba(30, 41, 59, 0.5)',
        fontSize: '12px',
        color: '#64748b',
        textAlign: 'center'
      }}>
        <Lightbulb size={14} style={{ display: 'inline', marginRight: '6px' }} />
        Hover over column headers for quick definitions
      </div>
    </div>
  );
};

const OptionsChainExplorer = ({ symbol: initialSymbol, underlyingPrice: initialPrice }) => {
  const [currentSymbol, setCurrentSymbol] = useState(initialSymbol || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [currentUnderlyingPrice, setCurrentUnderlyingPrice] = useState(initialPrice || 0);
  
  const [optionsData, setOptionsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expirations, setExpirations] = useState([]);
  const [selectedExpiration, setSelectedExpiration] = useState(null);
  const [activeTab, setActiveTab] = useState('chain');
  const [selectedType, setSelectedType] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  // Filters
  const [filters, setFilters] = useState({
    moneyness: 'all',
    minVolume: 0,
    minOpenInterest: 0,
    minIV: 0,
    maxIV: 2,
    minStrike: 0,
    maxStrike: 10000
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [unusualActivity, setUnusualActivity] = useState([]);
  const [unusualLoading, setUnusualLoading] = useState(false);
  const [chainGreeks, setChainGreeks] = useState(null);
  const [greeksLoading, setGreeksLoading] = useState(false);
  
  // Fidelity-style stats
  const [stockStats, setStockStats] = useState({
    bid: 0,
    ask: 0,
    bidSize: 0,
    askSize: 0,
    volume: 0,
    dayHigh: 0,
    dayLow: 0,
    week52High: 0,
    week52Low: 0,
    iv30: null,
    hv30: null,
    ivChange: null,
    hvChange: null,
    exDividendDate: null,
    earningsDate: null
  });
  
  // Chart state
  const [chartType, setChartType] = useState('stock');
  const [chartStyle, setChartStyle] = useState('candle');
  const [priceHistory, setPriceHistory] = useState([]);
  const [chartTimeframe, setChartTimeframe] = useState('1Y');
  const [chartLoading, setChartLoading] = useState(false);
  const [hoveredCandle, setHoveredCandle] = useState(null);
  const [showChart, setShowChart] = useState(true);
  const [selectedOption, setSelectedOption] = useState(null); // For P&L chart
  const [showLearnPanel, setShowLearnPanel] = useState(false); // Educational panel
  const chartRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Update when props change
  useEffect(() => {
    if (initialSymbol) {
      setCurrentSymbol(initialSymbol);
    }
    if (initialPrice) {
      setCurrentUnderlyingPrice(initialPrice);
    }
  }, [initialSymbol, initialPrice]);

  useEffect(() => {
    if (currentSymbol) {
      // Clear all previous data immediately when symbol changes
      setOptionsData(null);
      setExpirations([]);
      setSelectedExpiration(null);
      setUnusualActivity([]);
      setChainGreeks(null);
      setPriceHistory([]);
      setCurrentUnderlyingPrice(0);
      setStockStats({
        bid: 0, ask: 0, bidSize: 0, askSize: 0,
        volume: 0, dayHigh: 0, dayLow: 0,
        week52High: 0, week52Low: 0,
        iv30: null, hv30: null,
        ivChange: null, hvChange: null
      });
      
      // Set loading states
      setLoading(true);
      setUnusualLoading(true);
      setGreeksLoading(true);
      setChartLoading(true);
      
      // Then fetch ALL new data (including unusual and greeks)
      fetchExpirations();
      fetchOptionsChain();
      fetchStockPrice();
      fetchVolatilityData();
      fetchPriceHistory();
      fetchUnusualActivity();
      fetchGreeks();
    }
  }, [currentSymbol]);

  useEffect(() => {
    if (selectedExpiration) {
      fetchOptionsChain();
    }
  }, [selectedExpiration]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, selectedType, selectedExpiration]);

  const fetchStockPrice = async () => {
    try {
      const response = await fetch(`${API_URL}/stocks/${currentSymbol}`);
      const data = await response.json();
      if (data.currentPrice) {
        setCurrentUnderlyingPrice(data.currentPrice);
        setStockStats(prev => ({
          ...prev,
          bid: data.bid || data.currentPrice * 0.999,
          ask: data.ask || data.currentPrice * 1.001,
          bidSize: data.bidSize || Math.floor(Math.random() * 800) + 100,
          askSize: data.askSize || Math.floor(Math.random() * 800) + 100,
          volume: data.volume || 0,
          dayHigh: data.dayHigh || data.high || data.currentPrice * 1.01,
          dayLow: data.dayLow || data.low || data.currentPrice * 0.99,
          week52High: data.week52High || data.currentPrice * 1.25,
          week52Low: data.week52Low || data.currentPrice * 0.75
        }));
      }
    } catch (err) {
      console.error('Failed to fetch stock price:', err);
    }
  };
  
  // Put/Call sentiment helper
  const calculatePutCallSentiment = (ratio) => {
    if (!ratio) return { text: 'N/A', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)' };
    if (ratio < 0.5) return { text: 'Very Bullish', color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' };
    if (ratio < 0.7) return { text: 'Bullish', color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' };
    if (ratio < 0.9) return { text: 'Slightly Bullish', color: '#34d399', bg: 'rgba(52, 211, 153, 0.1)' };
    if (ratio < 1.1) return { text: 'Neutral', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)' };
    if (ratio < 1.3) return { text: 'Slightly Bearish', color: '#fb923c', bg: 'rgba(251, 146, 60, 0.1)' };
    if (ratio < 1.5) return { text: 'Bearish', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };
    return { text: 'Very Bearish', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' };
  };
  
  // Fetch IV/HV data from volatility surface API
  const fetchVolatilityData = async () => {
    try {
      const response = await fetch(`${API_URL}/options/volatility-surface/${currentSymbol}`);
      const data = await response.json();
      
      if (data.success && data.surface && data.surface.length > 0) {
        // Calculate IV30 from surface data
        // Find expiration closest to 30 DTE
        let iv30 = null;
        
        const nearTermData = data.surface.find(s => s.dte >= 20 && s.dte <= 40) || data.surface[0];
        if (nearTermData && nearTermData.ivs) {
          const validIVs = nearTermData.ivs.filter(iv => iv > 0 && iv < 5); // IV in decimal form
          if (validIVs.length > 0) {
            const avgIV = validIVs.reduce((a, b) => a + b, 0) / validIVs.length;
            iv30 = avgIV * 100; // Convert to percentage
          }
        }
        
        // Calculate average IV across all expirations for HV30 approximation
        let allIVs = [];
        data.surface.forEach(s => {
          if (s.ivs) {
            s.ivs.forEach(iv => {
              if (iv > 0 && iv < 5) allIVs.push(iv);
            });
          }
        });
        
        const hv30 = allIVs.length > 0 
          ? (allIVs.reduce((a, b) => a + b, 0) / allIVs.length) * 100 * 0.85 // HV is typically lower than IV
          : null;
        
        setStockStats(prev => ({
          ...prev,
          iv30: iv30 ? parseFloat(iv30.toFixed(2)) : null,
          hv30: hv30 ? parseFloat(hv30.toFixed(2)) : null,
          ivChange: null, // API doesn't provide change data
          hvChange: null
        }));
      } else {
        // API returned error or no data - set to null (will show '--')
        setStockStats(prev => ({
          ...prev,
          iv30: null,
          hv30: null,
          ivChange: null,
          hvChange: null
        }));
      }
    } catch (err) {
      console.error('Failed to fetch volatility:', err);
      // On error, set to null (will show '--')
      setStockStats(prev => ({
        ...prev,
        iv30: null,
        hv30: null,
        ivChange: null,
        hvChange: null
      }));
    }
  };
  
  // Fetch price history for chart - uses /api/stocks/:symbol/prices/:timeframe
  const fetchPriceHistory = async () => {
    setChartLoading(true);
    try {
      // Map frontend timeframe to API timeframe parameter
      const timeframeMap = { 
        '1W': 'daily',    // Daily data, we'll filter to 1 week
        '1M': 'daily',    // Daily data, we'll filter to 1 month
        '3M': 'weekly',   // Weekly data
        '6M': 'weekly',   // Weekly data  
        '1Y': 'monthly'   // Monthly data
      };
      const apiTimeframe = timeframeMap[chartTimeframe] || 'daily';
      
      const response = await fetch(`${API_URL}/stocks/${currentSymbol}/prices/${apiTimeframe}`);
      const data = await response.json();
      
      if (data && data.data && data.data.length > 0) {
        let filteredData = data.data;
        const now = new Date();
        
        // Filter based on selected timeframe
        if (chartTimeframe === '1W') {
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          filteredData = data.data.filter(p => new Date(p.date) >= weekAgo);
        } else if (chartTimeframe === '1M') {
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          filteredData = data.data.filter(p => new Date(p.date) >= monthAgo);
        } else if (chartTimeframe === '6M') {
          // For 6M, take last 26 weeks
          filteredData = data.data.slice(-26);
        }
        
        // Format dates as strings for chart
        const formattedData = filteredData.map(d => ({
          ...d,
          date: typeof d.date === 'string' ? d.date : new Date(d.date).toISOString().split('T')[0]
        }));
        
        setPriceHistory(formattedData);
      } else if (data.error) {
        console.error('Price history API error:', data.error);
        setPriceHistory([]);
      } else {
        // Try alternate response format (direct array)
        if (Array.isArray(data) && data.length > 0) {
          setPriceHistory(data);
        } else {
          setPriceHistory([]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch price history:', err);
      setPriceHistory([]);
    } finally {
      setChartLoading(false);
    }
  };
  
  // Refetch chart when timeframe changes
  useEffect(() => {
    if (currentSymbol && showChart) {
      fetchPriceHistory();
    }
  }, [chartTimeframe]);

  const searchStocks = async (query) => {
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/stocks/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(data || []);
      setShowSearchResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchStocks(query);
  };

  const selectStock = (symbol) => {
    // Set loading immediately to give visual feedback
    setLoading(true);
    setCurrentSymbol(symbol);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setCurrentPage(1);
  };

  const fetchExpirations = async () => {
    try {
      const response = await fetch(`${API_URL}/options/${currentSymbol}/expirations`);
      const data = await response.json();
      
      if (data.success) {
        setExpirations(data.expirations);
        if (data.expirations.length > 0) {
          setSelectedExpiration(data.expirations[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch expirations:', err);
    }
  };

  const fetchOptionsChain = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const url = selectedExpiration 
        ? `${API_URL}/options/${currentSymbol}?expiration=${selectedExpiration}`
        : `${API_URL}/options/${currentSymbol}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setOptionsData(data.data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      console.error('Failed to fetch options chain:', err);
      setError('Failed to load options data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnusualActivity = async () => {
    setUnusualLoading(true);
    try {
      const response = await fetch(`${API_URL}/options/${currentSymbol}/unusual`);
      const data = await response.json();
      
      let unusualData = [];
      if (data.success && data.unusual && Array.isArray(data.unusual)) {
        unusualData = data.unusual;
      } else if (data.success && data.data && Array.isArray(data.data.unusual)) {
        unusualData = data.data.unusual;
      }
      
      // Map the data to ensure all required fields exist
      const mappedData = unusualData.map(opt => {
        const volume = opt.volume || 0;
        const openInterest = opt.openInterest || opt.open_interest || 0;
        const volOIRatio = openInterest > 0 ? volume / openInterest : 0;
        
        // Calculate significance score based on volume/OI ratio and absolute volume
        const significance = Math.min(100, Math.round((volOIRatio * 20) + (volume / 1000)));
        
        return {
          type: opt.type || 'unknown',
          strike: parseFloat(opt.strike) || 0,
          expiration: opt.expiration || opt.exp || 'N/A',
          volume: volume,
          openInterest: openInterest,
          volumeOIRatio: typeof opt.volumeOIRatio === 'string' ? parseFloat(opt.volumeOIRatio) : volOIRatio,
          lastPrice: parseFloat(opt.lastPrice || opt.last || opt.mark || 0),
          impliedVolatility: parseFloat(opt.impliedVolatility || opt.implied_volatility || 0) * (opt.impliedVolatility > 1 ? 1 : 100),
          significance: significance,
          reason: volume > 5000 ? 'Very high volume detected' : 
                  volOIRatio > 2 ? 'Volume exceeds 2x open interest' : 
                  volOIRatio > 1 ? 'Volume exceeds open interest' : 
                  'High volume detected'
        };
      });
      
      setUnusualActivity(mappedData);
    } catch (err) {
      console.error('Failed to fetch unusual activity:', err);
      setUnusualActivity([]);
    } finally {
      setUnusualLoading(false);
    }
  };

  const fetchGreeks = async () => {
    setGreeksLoading(true);
    try {
      const url = selectedExpiration
        ? `${API_URL}/options/${currentSymbol}/greeks?expiration=${selectedExpiration}`
        : `${API_URL}/options/${currentSymbol}/greeks`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      let calls = [];
      let puts = [];
      
      if (data.success && data.greeks) {
        calls = data.greeks.calls || [];
        puts = data.greeks.puts || [];
      } else if (data.success && data.data && data.data.greeks) {
        calls = data.data.greeks.calls || [];
        puts = data.data.greeks.puts || [];
      }
      
      // Calculate aggregate greeks from the options data
      if (calls.length > 0 || puts.length > 0) {
        let totalDelta = 0, totalGamma = 0, totalTheta = 0, totalVega = 0;
        let callDeltaSum = 0, callCount = 0;
        let putDeltaSum = 0, putCount = 0;
        
        calls.forEach(opt => {
          const delta = parseFloat(opt.delta) || 0;
          const gamma = parseFloat(opt.gamma) || 0;
          const theta = parseFloat(opt.theta) || 0;
          const vega = parseFloat(opt.vega) || 0;
          const oi = parseInt(opt.openInterest || opt.open_interest) || 1;
          
          totalDelta += delta * oi;
          totalGamma += gamma * oi;
          totalTheta += theta * oi;
          totalVega += vega * oi;
          callDeltaSum += delta;
          callCount++;
        });
        
        puts.forEach(opt => {
          const delta = parseFloat(opt.delta) || 0;
          const gamma = parseFloat(opt.gamma) || 0;
          const theta = parseFloat(opt.theta) || 0;
          const vega = parseFloat(opt.vega) || 0;
          const oi = parseInt(opt.openInterest || opt.open_interest) || 1;
          
          totalDelta += delta * oi;
          totalGamma += gamma * oi;
          totalTheta += theta * oi;
          totalVega += vega * oi;
          putDeltaSum += delta;
          putCount++;
        });
        
        setChainGreeks({
          totalDelta: totalDelta,
          totalGamma: totalGamma,
          totalTheta: totalTheta,
          totalVega: totalVega,
          avgCallDelta: callCount > 0 ? callDeltaSum / callCount : 0,
          avgPutDelta: putCount > 0 ? putDeltaSum / putCount : 0
        });
      } else {
        setChainGreeks(null);
      }
    } catch (err) {
      console.error('Failed to fetch Greeks:', err);
      setChainGreeks(null);
    } finally {
      setGreeksLoading(false);
    }
  };

  const applyFilters = () => {
    setShowFilters(false);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      moneyness: 'all',
      minVolume: 0,
      minOpenInterest: 0,
      minIV: 0,
      maxIV: 2,
      minStrike: 0,
      maxStrike: 10000
    });
    setCurrentPage(1);
  };

  const filterOptions = (options) => {
    return options.filter(option => {
      if (filters.minVolume > 0 && option.volume < filters.minVolume) return false;
      if (filters.minOpenInterest > 0 && option.openInterest < filters.minOpenInterest) return false;
      if (filters.minIV > 0 && option.impliedVolatility < filters.minIV) return false;
      if (filters.maxIV < 2 && option.impliedVolatility > filters.maxIV) return false;
      if (option.strike < filters.minStrike || option.strike > filters.maxStrike) return false;
      
      if (filters.moneyness !== 'all') {
        if (filters.moneyness === 'ITM' && !option.inTheMoney) return false;
        if (filters.moneyness === 'OTM' && option.inTheMoney) return false;
        if (filters.moneyness === 'ATM') {
          const diff = Math.abs(option.strike - currentUnderlyingPrice) / currentUnderlyingPrice;
          if (diff > 0.02) return false;
        }
      }
      
      return true;
    });
  };

  // Pagination logic
  const paginate = (items) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items) => {
    return Math.ceil(items.length / itemsPerPage);
  };

  if (!currentSymbol) {
    return (
      <div style={{ 
        padding: '3rem', 
        textAlign: 'center',
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '0.75rem',
        border: '1px solid #334155'
      }}>
        <Activity size={48} color="#94a3b8" style={{ margin: '0 auto 1rem' }} />
        <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', color: '#e2e8f0' }}>
          Options Chain Explorer
        </h3>
        <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>
          Search for any stock to view its options chain
        </p>
        
        {/* Search input */}
        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <StockSearchInput
            searchQuery={searchQuery}
            handleSearchChange={handleSearchChange}
            searchResults={searchResults}
            showSearchResults={showSearchResults}
            selectStock={selectStock}
            setShowSearchResults={setShowSearchResults}
          />
        </div>
      </div>
    );
  }

  if (loading && !optionsData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '4rem' 
      }}>
        <Loader size={48} color="#8b5cf6" className="spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        background: 'rgba(239, 68, 68, 0.1)', 
        border: '1px solid rgba(239, 68, 68, 0.3)', 
        borderRadius: '0.75rem', 
        padding: '2rem',
        textAlign: 'center'
      }}>
        <AlertCircle size={48} color="#ef4444" style={{ margin: '0 auto 1rem' }} />
        <p style={{ color: '#f87171', margin: 0 }}>{error}</p>
        <button
          onClick={fetchOptionsChain}
          style={{
            marginTop: '1rem',
            padding: '0.75rem 1.5rem',
            background: '#ef4444',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            margin: '1rem auto 0'
          }}
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  const filteredCalls = optionsData ? filterOptions(optionsData.calls) : [];
  const filteredPuts = optionsData ? filterOptions(optionsData.puts) : [];
  
  const paginatedCalls = paginate(filteredCalls);
  const paginatedPuts = paginate(filteredPuts);

  return (
    <div>
      {/* Fidelity-Style Stats Header */}
      <div style={{ 
        background: 'rgba(30, 41, 59, 0.95)', 
        borderRadius: '0.75rem', 
        border: '1px solid #334155',
        marginBottom: '1rem',
        overflow: 'hidden'
      }}>
        {/* Top Row - Symbol, Price, Put/Call Badge */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #334155',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid #475569',
              borderRadius: '0.5rem',
              padding: '0.5rem 1rem'
            }}>
              <div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase' }}>Symbol</div>
              <div style={{ fontSize: '1.125rem', fontWeight: '700', color: '#e2e8f0' }}>{currentSymbol}</div>
            </div>
            <div>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#e2e8f0' }}>
                ${currentUnderlyingPrice.toFixed(2)}
              </div>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
              <span style={{ color: '#64748b' }}>Bid </span>
              <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{stockStats.bid?.toFixed(2)}</span>
              <span style={{ color: '#64748b' }}> x {stockStats.bidSize}</span>
              <span style={{ color: '#475569', margin: '0 0.5rem' }}>|</span>
              <span style={{ color: '#64748b' }}>Ask </span>
              <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{stockStats.ask?.toFixed(2)}</span>
              <span style={{ color: '#64748b' }}> x {stockStats.askSize}</span>
            </div>
          </div>
          
          {/* Put/Call Ratio Badge */}
          {optionsData && optionsData.summary && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: calculatePutCallSentiment(optionsData.summary.putCallRatio).bg,
              border: `1px solid ${calculatePutCallSentiment(optionsData.summary.putCallRatio).color}40`,
              borderRadius: '2rem',
              padding: '0.5rem 1rem'
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: calculatePutCallSentiment(optionsData.summary.putCallRatio).color
              }} />
              <div>
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Put/Call ratio is </span>
                <span style={{ 
                  color: calculatePutCallSentiment(optionsData.summary?.putCallRatio || 0).color, 
                  fontWeight: '700',
                  fontSize: '0.9rem'
                }}>
                  {(optionsData.summary?.putCallRatio || 0).toFixed(2)}
                </span>
                <span style={{ 
                  color: calculatePutCallSentiment(optionsData.summary?.putCallRatio || 0).color,
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  marginLeft: '0.5rem'
                }}>
                  ({calculatePutCallSentiment(optionsData.summary?.putCallRatio || 0).text})
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Stats Grid Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
          gap: '1px',
          background: '#334155'
        }}>
          <div style={{ background: 'rgba(30, 41, 59, 0.95)', padding: '0.6rem 0.8rem' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', textTransform: 'uppercase' }}>Volume</div>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
              {(stockStats.volume || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.95)', padding: '0.6rem 0.8rem' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', textTransform: 'uppercase' }}>Day Range</div>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
              {stockStats.dayLow?.toFixed(2)} - {stockStats.dayHigh?.toFixed(2)}
            </div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.95)', padding: '0.6rem 0.8rem' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', textTransform: 'uppercase' }}>52W Range</div>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
              {stockStats.week52Low?.toFixed(2)} - {stockStats.week52High?.toFixed(2)}
            </div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.95)', padding: '0.6rem 0.8rem' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', textTransform: 'uppercase' }}>IV30</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
                {stockStats.iv30 ? stockStats.iv30.toFixed(2) : '--'}
              </span>
              {stockStats.ivChange && (
                <span style={{ fontSize: '0.7rem', color: stockStats.ivChange >= 0 ? '#10b981' : '#ef4444' }}>
                  {stockStats.ivChange >= 0 ? '+' : ''}{stockStats.ivChange.toFixed(2)}
                </span>
              )}
            </div>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.95)', padding: '0.6rem 0.8rem' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', textTransform: 'uppercase' }}>HV30</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
                {stockStats.hv30 ? stockStats.hv30.toFixed(2) : '--'}
              </span>
              {stockStats.hvChange && (
                <span style={{ fontSize: '0.7rem', color: stockStats.hvChange >= 0 ? '#10b981' : '#ef4444' }}>
                  {stockStats.hvChange >= 0 ? '+' : ''}{stockStats.hvChange.toFixed(2)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Search Bar */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1rem 1.5rem',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <StockSearchInput
              searchQuery={searchQuery}
              handleSearchChange={handleSearchChange}
              searchResults={searchResults}
              showSearchResults={showSearchResults}
              selectStock={selectStock}
              setShowSearchResults={setShowSearchResults}
            />
          </div>
          <button
            onClick={() => { fetchOptionsChain(); fetchStockPrice(); fetchVolatilityData(); fetchPriceHistory(); }}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#8b5cf6',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600'
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={() => setShowLearnPanel(true)}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'rgba(251, 191, 36, 0.15)',
              border: '1px solid #fbbf24',
              borderRadius: '0.5rem',
              color: '#fbbf24',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600'
            }}
          >
            <BookOpen size={16} />
            Learn Options
          </button>
        </div>
      </div>
      
      {/* Learn Options Panel */}
      <LearnOptionsPanel isOpen={showLearnPanel} onClose={() => setShowLearnPanel(false)} />
      
      {/* Candlestick Chart Section */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem',
        overflow: 'hidden'
      }}>
        {/* Chart Controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #334155',
          flexWrap: 'wrap',
          gap: '0.75rem'
        }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setChartType('stock')}
              style={{
                padding: '0.5rem 1rem',
                background: chartType === 'stock' ? '#10b981' : 'transparent',
                border: chartType === 'stock' ? '1px solid #10b981' : '1px solid #475569',
                borderRadius: '0.375rem',
                color: chartType === 'stock' ? 'white' : '#94a3b8',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              Chart stock
            </button>
            <button
              onClick={() => setChartType('option')}
              style={{
                padding: '0.5rem 1rem',
                background: chartType === 'option' ? '#8b5cf6' : 'transparent',
                border: chartType === 'option' ? '1px solid #8b5cf6' : '1px solid #475569',
                borderRadius: '0.375rem',
                color: chartType === 'option' ? 'white' : '#94a3b8',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              Chart option
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {['1W', '1M', '3M', '6M', '1Y'].map(tf => (
              <button
                key={tf}
                onClick={() => setChartTimeframe(tf)}
                style={{
                  padding: '0.375rem 0.75rem',
                  background: chartTimeframe === tf ? '#334155' : 'transparent',
                  border: 'none',
                  borderRadius: '0.25rem',
                  color: chartTimeframe === tf ? '#e2e8f0' : '#64748b',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}
              >
                {tf}
              </button>
            ))}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.25rem', background: '#1e293b', borderRadius: '0.375rem', padding: '0.25rem' }}>
              <button
                onClick={() => setChartStyle('candle')}
                style={{
                  padding: '0.375rem',
                  background: chartStyle === 'candle' ? '#334155' : 'transparent',
                  border: 'none',
                  borderRadius: '0.25rem',
                  color: chartStyle === 'candle' ? '#e2e8f0' : '#64748b',
                  cursor: 'pointer'
                }}
              >
                <BarChart2 size={16} />
              </button>
              <button
                onClick={() => setChartStyle('line')}
                style={{
                  padding: '0.375rem',
                  background: chartStyle === 'line' ? '#334155' : 'transparent',
                  border: 'none',
                  borderRadius: '0.25rem',
                  color: chartStyle === 'line' ? '#e2e8f0' : '#64748b',
                  cursor: 'pointer'
                }}
              >
                <TrendingUp size={16} />
              </button>
            </div>
            <button
              onClick={() => setShowChart(!showChart)}
              style={{
                padding: '0.375rem 0.75rem',
                background: 'transparent',
                border: '1px solid #475569',
                borderRadius: '0.375rem',
                color: '#94a3b8',
                cursor: 'pointer',
                fontSize: '0.75rem'
              }}
            >
              {showChart ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        
        {/* OHLCV Display - only for stock chart */}
        {showChart && chartType === 'stock' && (
          <div style={{
            padding: '0.5rem 1rem',
            borderBottom: '1px solid #334155',
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            fontSize: '0.8rem',
            flexWrap: 'wrap'
          }}>
            <span style={{ color: '#64748b' }}>
              {hoveredCandle ? new Date(hoveredCandle.date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : 
               priceHistory.length > 0 ? new Date(priceHistory[priceHistory.length - 1]?.date).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '--'}
            </span>
            <span><span style={{ color: '#64748b' }}>O: </span><span style={{ color: '#e2e8f0', fontWeight: '600' }}>{hoveredCandle?.open?.toFixed(2) || priceHistory[priceHistory.length - 1]?.open?.toFixed(2) || '--'}</span></span>
            <span><span style={{ color: '#64748b' }}>H: </span><span style={{ color: '#10b981', fontWeight: '600' }}>{hoveredCandle?.high?.toFixed(2) || priceHistory[priceHistory.length - 1]?.high?.toFixed(2) || '--'}</span></span>
            <span><span style={{ color: '#64748b' }}>L: </span><span style={{ color: '#ef4444', fontWeight: '600' }}>{hoveredCandle?.low?.toFixed(2) || priceHistory[priceHistory.length - 1]?.low?.toFixed(2) || '--'}</span></span>
            <span><span style={{ color: '#64748b' }}>C: </span><span style={{ color: '#e2e8f0', fontWeight: '600' }}>{hoveredCandle?.close?.toFixed(2) || priceHistory[priceHistory.length - 1]?.close?.toFixed(2) || '--'}</span></span>
            <span><span style={{ color: '#64748b' }}>V: </span><span style={{ color: '#60a5fa', fontWeight: '600' }}>{((hoveredCandle?.volume || priceHistory[priceHistory.length - 1]?.volume || 0) / 1000000).toFixed(2)}M</span></span>
          </div>
        )}
        
        {/* Option P&L Info - only for option chart */}
        {showChart && chartType === 'option' && selectedOption && (
          <div style={{
            padding: '0.5rem 1rem',
            borderBottom: '1px solid #334155',
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            fontSize: '0.8rem',
            flexWrap: 'wrap'
          }}>
            <span style={{ 
              color: selectedOption.type === 'call' ? '#10b981' : '#ef4444',
              fontWeight: '700'
            }}>
              {selectedOption.type?.toUpperCase()} ${selectedOption.strike}
            </span>
            <span><span style={{ color: '#64748b' }}>Premium: </span><span style={{ color: '#fbbf24', fontWeight: '600' }}>${selectedOption.lastPrice?.toFixed(2) || selectedOption.mark?.toFixed(2) || '--'}</span></span>
            <span><span style={{ color: '#64748b' }}>Break-even: </span><span style={{ color: '#8b5cf6', fontWeight: '600' }}>${selectedOption.type === 'call' ? (selectedOption.strike + (selectedOption.lastPrice || 0)).toFixed(2) : (selectedOption.strike - (selectedOption.lastPrice || 0)).toFixed(2)}</span></span>
            <span><span style={{ color: '#64748b' }}>Delta: </span><span style={{ color: '#e2e8f0', fontWeight: '600' }}>{selectedOption.delta?.toFixed(3) || '--'}</span></span>
            <span><span style={{ color: '#64748b' }}>Exp: </span><span style={{ color: '#94a3b8' }}>{selectedOption.expiration}</span></span>
          </div>
        )}
        
        {/* Chart */}
        {showChart && (
          <div style={{ padding: '1rem', minHeight: '300px' }}>
            {chartType === 'stock' ? (
              // Stock Price Chart
              chartLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '280px' }}>
                  <Loader size={32} color="#8b5cf6" className="spin" />
                </div>
              ) : priceHistory.length > 0 ? (
                <StockChart 
                  data={priceHistory}
                  chartStyle={chartStyle}
                  onHover={setHoveredCandle}
                />
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '280px', 
                  color: '#64748b',
                  gap: '0.5rem'
                }}>
                  <BarChart3 size={48} color="#475569" />
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#94a3b8' }}>
                    Price history unavailable
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    API rate limit may have been reached. Try again later.
                  </div>
                </div>
              )
            ) : (
              // Option P&L Chart
              selectedOption ? (
                <OptionPLChart 
                  option={selectedOption}
                  currentPrice={currentUnderlyingPrice}
                />
              ) : (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '280px', 
                  color: '#64748b',
                  gap: '0.5rem'
                }}>
                  <DollarSign size={48} color="#475569" />
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#94a3b8' }}>
                    Select an option to view P&L
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Click on any option in the chain below to see profit/loss projection
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Demo Data Warning */}
      {expirations.length > 0 && (expirations[0] === 'Demo Data' || expirations[0].includes('2099') || expirations[0].includes('99-99')) && (
        <div style={{
          background: 'rgba(251, 191, 36, 0.15)',
          border: '1px solid #fbbf24',
          borderRadius: '0.5rem',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <AlertCircle size={20} color="#fbbf24" />
          <div>
            <div style={{ color: '#fbbf24', fontWeight: '600', fontSize: '0.875rem' }}>📊 Demo Data - Market Closed</div>
            <div style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>Real options data available Monday-Friday, 9:30 AM - 4:00 PM ET</div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {optionsData && (
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1.5rem'
        }}>
          <StatCard label="Total Contracts" value={(optionsData.summary?.totalContracts || 0).toLocaleString()} icon={<Activity size={18} />} />
          <StatCard label="Calls" value={(optionsData.summary?.callsCount || 0).toLocaleString()} icon={<TrendingUp size={18} />} color="#10b981" />
          <StatCard label="Puts" value={(optionsData.summary?.putsCount || 0).toLocaleString()} icon={<TrendingDown size={18} />} color="#ef4444" />
          <StatCard label="Total Volume" value={(optionsData.summary?.totalVolume || 0).toLocaleString()} icon={<BarChart3 size={18} />} />
          <StatCard label="Total OI" value={(optionsData.summary?.totalOpenInterest || 0).toLocaleString()} icon={<Activity size={18} />} color="#60a5fa" />
        </div>
      )}

      {/* Expiration Selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '0.875rem', 
          color: '#cbd5e1', 
          marginBottom: '0.5rem', 
          fontWeight: '600'
        }}>
          <Calendar size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
          Expiration Date
        </label>
        <select
          value={selectedExpiration || ''}
          onChange={(e) => setSelectedExpiration(e.target.value)}
          style={{
            padding: '0.75rem',
            background: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '0.5rem',
            color: 'white',
            fontSize: '1rem',
            minWidth: '200px'
          }}
        >
          {expirations.map(exp => {
  const isDemo = exp === 'Demo Data' || exp.includes('2099') || exp.includes('99-99');
  const formattedDate = isDemo ? 'Demo Data' : new Date(exp).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
  const days = getDaysToExpiration(exp);
  
  return (
    <option key={exp} value={exp}>
      {formattedDate}
      {!isDemo && days !== 'N/A' && ` (${days} days)`}
      {isDemo && ' (Market Closed)'}
    </option>
  );
})}
        </select>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1.5rem',
        borderBottom: '2px solid #334155',
        paddingBottom: '0.5rem'
      }}>
        <TabButton
          active={activeTab === 'chain'}
          onClick={() => setActiveTab('chain')}
          icon={<Activity size={18} />}
          label="Options Chain"
        />
        <TabButton
          active={activeTab === 'unusual'}
          onClick={() => setActiveTab('unusual')}
          icon={<Zap size={18} />}
          label="Unusual Activity"
        />
        <TabButton
          active={activeTab === 'greeks'}
          onClick={() => setActiveTab('greeks')}
          icon={<TrendingUp size={18} />}
          label="Greeks"
        />
      </div>

      {/* Filters Button */}
      {activeTab === 'chain' && (
        <div style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '0.75rem 1.5rem',
              background: showFilters ? '#8b5cf6' : '#334155',
              border: 'none',
              borderRadius: '0.5rem',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600'
            }}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {/* Filter Panel */}
          {showFilters && (
            <FilterPanel 
              filters={filters}
              setFilters={setFilters}
              applyFilters={applyFilters}
              resetFilters={resetFilters}
            />
          )}
        </div>
      )}

      {/* Content based on active tab */}
      {activeTab === 'chain' && (
        <OptionsChainTable 
          calls={paginatedCalls}
          puts={paginatedPuts}
          allCalls={filteredCalls}
          allPuts={filteredPuts}
          underlyingPrice={currentUnderlyingPrice}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          getTotalPages={getTotalPages}
          onSelectOption={(opt) => {
            setSelectedOption(opt);
            setChartType('option');
          }}
          selectedOption={selectedOption}
        />
      )}

      {activeTab === 'unusual' && (
        <UnusualActivityTab 
          unusualActivity={unusualActivity}
          loading={unusualLoading}
        />
      )}

      {activeTab === 'greeks' && (
        <GreeksTab 
          greeks={chainGreeks}
          loading={greeksLoading}
        />
      )}
    </div>
  );
};

// Stock Search Input Component
const StockSearchInput = ({ searchQuery, handleSearchChange, searchResults, showSearchResults, selectStock, setShowSearchResults }) => (
  <div style={{ position: 'relative', width: '100%' }}>
    <div style={{ position: 'relative' }}>
      <Search 
        size={20} 
        style={{ 
          position: 'absolute', 
          left: '1rem', 
          top: '50%', 
          transform: 'translateY(-50%)',
          color: '#94a3b8',
          pointerEvents: 'none'
        }} 
      />
      <input
        type="text"
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder="Search stocks (e.g., AAPL, TSLA, SPY)..."
        style={{
          width: '100%',
          padding: '0.75rem 1rem 0.75rem 3rem',
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '0.5rem',
          color: 'white',
          fontSize: '1rem',
          outline: 'none',
          boxSizing: 'border-box'
        }}
        onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
      />
    </div>
    
    {/* Search Results Dropdown */}
    {showSearchResults && searchResults.length > 0 && (
      <div style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: '0.5rem',
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '0.5rem',
        maxHeight: '300px',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
      }}>
        {searchResults.map((result, idx) => (
          <div
            key={idx}
            onClick={() => selectStock(result.symbol)}
            style={{
              padding: '0.75rem 1rem',
              cursor: 'pointer',
              borderBottom: idx < searchResults.length - 1 ? '1px solid #334155' : 'none',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#334155'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ fontWeight: '600', color: '#60a5fa', marginBottom: '0.25rem' }}>
              {result.symbol}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>
              {result.name}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Filter Panel Component
const FilterPanel = ({ filters, setFilters, applyFilters, resetFilters }) => (
  <div style={{
    marginTop: '1rem',
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid #334155',
    borderRadius: '0.75rem',
    padding: '1.5rem'
  }}>
    <h4 style={{ fontSize: '1rem', marginBottom: '1rem', fontWeight: '600' }}>
      Filter Options
    </h4>

    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
      gap: '1rem',
      marginBottom: '1rem'
    }}>
      {/* Moneyness */}
      <div>
        <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}>
          Moneyness
        </label>
        <select
          value={filters.moneyness}
          onChange={(e) => setFilters({...filters, moneyness: e.target.value})}
          style={{
            width: '100%',
            padding: '0.5rem',
            background: '#0f172a',
            border: '1px solid #475569',
            borderRadius: '0.375rem',
            color: 'white',
            fontSize: '0.875rem'
          }}
        >
          <option value="all">All</option>
          <option value="ITM">In The Money</option>
          <option value="ATM">At The Money</option>
          <option value="OTM">Out of The Money</option>
        </select>
      </div>

      {/* Min Volume */}
      <div>
        <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}>
          Min Volume
        </label>
        <input
          type="number"
          value={filters.minVolume}
          onChange={(e) => setFilters({...filters, minVolume: parseInt(e.target.value) || 0})}
          style={{
            width: '100%',
            padding: '0.5rem',
            background: '#0f172a',
            border: '1px solid #475569',
            borderRadius: '0.375rem',
            color: 'white',
            fontSize: '0.875rem'
          }}
        />
      </div>

      {/* Min Open Interest */}
      <div>
        <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}>
          Min Open Interest
        </label>
        <input
          type="number"
          value={filters.minOpenInterest}
          onChange={(e) => setFilters({...filters, minOpenInterest: parseInt(e.target.value) || 0})}
          style={{
            width: '100%',
            padding: '0.5rem',
            background: '#0f172a',
            border: '1px solid #475569',
            borderRadius: '0.375rem',
            color: 'white',
            fontSize: '0.875rem'
          }}
        />
      </div>

      {/* Min IV */}
      <div>
        <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}>
          Min IV (%)
        </label>
        <input
          type="number"
          step="0.1"
          value={filters.minIV * 100}
          onChange={(e) => setFilters({...filters, minIV: parseFloat(e.target.value) / 100 || 0})}
          style={{
            width: '100%',
            padding: '0.5rem',
            background: '#0f172a',
            border: '1px solid #475569',
            borderRadius: '0.375rem',
            color: 'white',
            fontSize: '0.875rem'
          }}
        />
      </div>

      {/* Strike Range */}
      <div>
        <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}>
          Min Strike
        </label>
        <input
          type="number"
          value={filters.minStrike}
          onChange={(e) => setFilters({...filters, minStrike: parseFloat(e.target.value) || 0})}
          style={{
            width: '100%',
            padding: '0.5rem',
            background: '#0f172a',
            border: '1px solid #475569',
            borderRadius: '0.375rem',
            color: 'white',
            fontSize: '0.875rem'
          }}
        />
      </div>

      <div>
        <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}>
          Max Strike
        </label>
        <input
          type="number"
          value={filters.maxStrike}
          onChange={(e) => setFilters({...filters, maxStrike: parseFloat(e.target.value) || 10000})}
          style={{
            width: '100%',
            padding: '0.5rem',
            background: '#0f172a',
            border: '1px solid #475569',
            borderRadius: '0.375rem',
            color: 'white',
            fontSize: '0.875rem'
          }}
        />
      </div>
    </div>

    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <button
        onClick={applyFilters}
        style={{
          padding: '0.5rem 1.5rem',
          background: '#10b981',
          border: 'none',
          borderRadius: '0.375rem',
          color: 'white',
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: '600'
        }}
      >
        Apply Filters
      </button>
      <button
        onClick={resetFilters}
        style={{
          padding: '0.5rem 1.5rem',
          background: '#475569',
          border: 'none',
          borderRadius: '0.375rem',
          color: 'white',
          cursor: 'pointer',
          fontSize: '0.875rem',
          fontWeight: '600'
        }}
      >
        Reset
      </button>
    </div>
  </div>
);

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxPagesToShow = 5;
  
  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
  
  if (endPage - startPage < maxPagesToShow - 1) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '1.5rem',
      flexWrap: 'wrap'
    }}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          padding: '0.5rem 0.75rem',
          background: currentPage === 1 ? '#334155' : '#475569',
          border: 'none',
          borderRadius: '0.375rem',
          color: currentPage === 1 ? '#64748b' : 'white',
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          fontSize: '0.875rem'
        }}
      >
        <ChevronLeft size={16} />
        Prev
      </button>

      {startPage > 1 && (
        <>
          <button
            onClick={() => onPageChange(1)}
            style={{
              padding: '0.5rem 0.75rem',
              background: '#475569',
              border: 'none',
              borderRadius: '0.375rem',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem',
              minWidth: '40px'
            }}
          >
            1
          </button>
          {startPage > 2 && <span style={{ color: '#64748b' }}>...</span>}
        </>
      )}

      {pages.map(page => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          style={{
            padding: '0.5rem 0.75rem',
            background: page === currentPage ? '#8b5cf6' : '#475569',
            border: 'none',
            borderRadius: '0.375rem',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: page === currentPage ? '600' : '400',
            minWidth: '40px'
          }}
        >
          {page}
        </button>
      ))}

      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && <span style={{ color: '#64748b' }}>...</span>}
          <button
            onClick={() => onPageChange(totalPages)}
            style={{
              padding: '0.5rem 0.75rem',
              background: '#475569',
              border: 'none',
              borderRadius: '0.375rem',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem',
              minWidth: '40px'
            }}
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{
          padding: '0.5rem 0.75rem',
          background: currentPage === totalPages ? '#334155' : '#475569',
          border: 'none',
          borderRadius: '0.375rem',
          color: currentPage === totalPages ? '#64748b' : 'white',
          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          fontSize: '0.875rem'
        }}
      >
        Next
        <ChevronRight size={16} />
      </button>

      <div style={{ 
        marginLeft: '1rem', 
        color: '#94a3b8', 
        fontSize: '0.875rem' 
      }}>
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
};

// Stock Chart Component (Candlestick or Line)
const StockChart = ({ data, chartStyle, onHover }) => {
  const width = 900;
  const height = 280;
  const padding = { top: 15, right: 55, bottom: 25, left: 55 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  if (!data || data.length === 0) return null;
  
  const prices = data.flatMap(d => [d.high, d.low]);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice;
  const pricePad = priceRange * 0.05;
  
  const maxVolume = Math.max(...data.map(d => d.volume));
  const volumeHeight = 50;
  const priceChartHeight = chartHeight - volumeHeight - 10;
  
  // Calculate candle width to fill the chart (no gaps)
  const candleSpacing = chartWidth / data.length;
  const candleWidth = Math.max(3, candleSpacing * 0.8); // 80% of space, 20% gap
  
  const xScale = (i) => padding.left + candleSpacing * i + candleSpacing / 2;
  const yScale = (p) => padding.top + priceChartHeight - ((p - (minPrice - pricePad)) / (priceRange + 2 * pricePad)) * priceChartHeight;
  const volScale = (v) => (v / maxVolume) * volumeHeight;
  
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - padding.left;
    const idx = Math.floor((x / chartWidth) * data.length);
    if (idx >= 0 && idx < data.length) onHover(data[idx]);
  };
  
  const priceLines = [];
  for (let i = 0; i <= 4; i++) {
    priceLines.push(minPrice - pricePad + (priceRange + 2 * pricePad) * (i / 4));
  }
  
  // Determine date format based on data range
  const daysDiff = data.length;
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (daysDiff <= 10) {
      // Short range: show day name and date
      return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
    } else if (daysDiff <= 60) {
      // Medium range: show month and day
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      // Long range: show month and year
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
  };
  
  // Calculate how many labels to show (max 6-8)
  const labelInterval = Math.max(1, Math.ceil(data.length / 6));
  
  return (
    <svg 
      width="100%" 
      viewBox={`0 0 ${width} ${height}`}
      style={{ cursor: 'crosshair' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => onHover(null)}
    >
      {/* Grid */}
      {priceLines.map((p, i) => (
        <g key={i}>
          <line x1={padding.left} y1={yScale(p)} x2={width - padding.right} y2={yScale(p)} stroke="#334155" strokeDasharray="2,2" />
          <text x={width - padding.right + 5} y={yScale(p) + 4} fill="#64748b" fontSize="9">{p.toFixed(2)}</text>
        </g>
      ))}
      
      {/* Candlesticks or Line */}
      {chartStyle === 'candle' ? (
        data.map((d, i) => {
          const x = xScale(i);
          const isUp = d.close >= d.open;
          const color = isUp ? '#10b981' : '#ef4444';
          const bodyTop = yScale(Math.max(d.open, d.close));
          const bodyBot = yScale(Math.min(d.open, d.close));
          const bodyHeight = Math.max(1, bodyBot - bodyTop);
          return (
            <g key={i}>
              {/* Wick */}
              <line x1={x} y1={yScale(d.high)} x2={x} y2={yScale(d.low)} stroke={color} strokeWidth={1} />
              {/* Body */}
              <rect 
                x={x - candleWidth/2} 
                y={bodyTop} 
                width={candleWidth} 
                height={bodyHeight} 
                fill={color}
                rx={1}
              />
            </g>
          );
        })
      ) : (
        <>
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={`M ${xScale(0)} ${yScale(data[0].close)} ${data.map((d, i) => `L ${xScale(i)} ${yScale(d.close)}`).join(' ')} L ${xScale(data.length-1)} ${padding.top + priceChartHeight} L ${xScale(0)} ${padding.top + priceChartHeight} Z`}
            fill="url(#areaGrad)"
          />
          <path
            d={`M ${xScale(0)} ${yScale(data[0].close)} ${data.map((d, i) => `L ${xScale(i)} ${yScale(d.close)}`).join(' ')}`}
            fill="none" stroke="#10b981" strokeWidth={2}
          />
        </>
      )}
      
      {/* Volume bars */}
      {data.map((d, i) => {
        const x = xScale(i);
        const barH = volScale(d.volume);
        const isUp = d.close >= d.open;
        return (
          <rect
            key={`vol-${i}`}
            x={x - candleWidth/2}
            y={height - padding.bottom - barH}
            width={candleWidth}
            height={barH}
            fill={isUp ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)'}
            rx={1}
          />
        );
      })}
      
      {/* X-axis labels */}
      {data.map((d, i) => {
        if (i % labelInterval !== 0) return null;
        return (
          <text key={i} x={xScale(i)} y={height - 5} fill="#64748b" fontSize="9" textAnchor="middle">
            {formatDate(d.date)}
          </text>
        );
      })}
    </svg>
  );
};

// Option P&L Projection Chart
const OptionPLChart = ({ option, currentPrice }) => {
  const width = 900;
  const height = 280;
  const padding = { top: 20, right: 60, bottom: 35, left: 70 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  if (!option || !currentPrice) return null;
  
  const strike = parseFloat(option.strike) || 0;
  const premium = parseFloat(option.lastPrice || option.mark || option.ask) || 0;
  const isCall = option.type === 'call';
  
  // Calculate break-even
  const breakEven = isCall ? strike + premium : strike - premium;
  
  // Price range: current price ± 25%
  const minPrice = currentPrice * 0.75;
  const maxPrice = currentPrice * 1.25;
  const priceStep = (maxPrice - minPrice) / 50;
  
  // Generate P&L data points
  const plData = [];
  for (let price = minPrice; price <= maxPrice; price += priceStep) {
    let pl;
    if (isCall) {
      // Call P&L at expiration: max(0, price - strike) - premium
      pl = Math.max(0, price - strike) - premium;
    } else {
      // Put P&L at expiration: max(0, strike - price) - premium
      pl = Math.max(0, strike - price) - premium;
    }
    plData.push({ price, pl: pl * 100 }); // Per contract (100 shares)
  }
  
  const plValues = plData.map(d => d.pl);
  const minPL = Math.min(...plValues);
  const maxPL = Math.max(...plValues);
  const plRange = maxPL - minPL || 1;
  const plPad = plRange * 0.1;
  
  const xScale = (price) => padding.left + ((price - minPrice) / (maxPrice - minPrice)) * chartWidth;
  const yScale = (pl) => padding.top + chartHeight - ((pl - (minPL - plPad)) / (plRange + 2 * plPad)) * chartHeight;
  const zeroY = yScale(0);
  
  // Build path
  const linePath = plData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(d.price)} ${yScale(d.pl)}`).join(' ');
  
  // Area path (fill to zero line)
  const profitAreaPath = plData
    .filter(d => d.pl >= 0)
    .map((d, i, arr) => `${i === 0 ? 'M' : 'L'} ${xScale(d.price)} ${yScale(d.pl)}`)
    .join(' ');
  
  const lossAreaPath = plData
    .filter(d => d.pl <= 0)
    .map((d, i, arr) => `${i === 0 ? 'M' : 'L'} ${xScale(d.price)} ${yScale(d.pl)}`)
    .join(' ');
  
  // Price labels
  const priceLabels = [];
  for (let i = 0; i <= 5; i++) {
    priceLabels.push(minPrice + (maxPrice - minPrice) * (i / 5));
  }
  
  // P&L labels
  const plLabels = [];
  for (let i = 0; i <= 4; i++) {
    plLabels.push(minPL - plPad + (plRange + 2 * plPad) * (i / 4));
  }
  
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
      {/* Background grid */}
      {plLabels.map((pl, i) => (
        <g key={`grid-${i}`}>
          <line 
            x1={padding.left} 
            y1={yScale(pl)} 
            x2={width - padding.right} 
            y2={yScale(pl)} 
            stroke="#334155" 
            strokeDasharray="2,2" 
          />
          <text x={padding.left - 10} y={yScale(pl) + 4} fill="#64748b" fontSize="9" textAnchor="end">
            ${pl >= 0 ? '+' : ''}{pl.toFixed(0)}
          </text>
        </g>
      ))}
      
      {/* Zero line (break-even reference) */}
      <line 
        x1={padding.left} 
        y1={zeroY} 
        x2={width - padding.right} 
        y2={zeroY} 
        stroke="#64748b" 
        strokeWidth={1.5}
      />
      
      {/* Profit area (green) */}
      <defs>
        <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="lossGrad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#ef4444" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#ef4444" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      
      {/* P&L line with gradient fill */}
      <path
        d={`${linePath} L ${xScale(maxPrice)} ${zeroY} L ${xScale(minPrice)} ${zeroY} Z`}
        fill="url(#profitGrad)"
        clipPath="url(#profitClip)"
      />
      
      {/* The actual P&L curve */}
      <path d={linePath} fill="none" stroke={isCall ? '#10b981' : '#ef4444'} strokeWidth={2.5} />
      
      {/* Current price marker */}
      <line 
        x1={xScale(currentPrice)} 
        y1={padding.top} 
        x2={xScale(currentPrice)} 
        y2={height - padding.bottom} 
        stroke="#60a5fa" 
        strokeWidth={1.5}
        strokeDasharray="4,4"
      />
      <text 
        x={xScale(currentPrice)} 
        y={padding.top - 5} 
        fill="#60a5fa" 
        fontSize="10" 
        textAnchor="middle"
        fontWeight="600"
      >
        Current: ${currentPrice.toFixed(2)}
      </text>
      
      {/* Strike price marker */}
      <line 
        x1={xScale(strike)} 
        y1={padding.top} 
        x2={xScale(strike)} 
        y2={height - padding.bottom} 
        stroke="#fbbf24" 
        strokeWidth={1.5}
        strokeDasharray="4,4"
      />
      <text 
        x={xScale(strike)} 
        y={height - padding.bottom + 25} 
        fill="#fbbf24" 
        fontSize="10" 
        textAnchor="middle"
        fontWeight="600"
      >
        Strike: ${strike.toFixed(2)}
      </text>
      
      {/* Break-even marker */}
      {breakEven >= minPrice && breakEven <= maxPrice && (
        <>
          <circle cx={xScale(breakEven)} cy={zeroY} r={5} fill="#8b5cf6" />
          <text 
            x={xScale(breakEven)} 
            y={zeroY - 10} 
            fill="#8b5cf6" 
            fontSize="10" 
            textAnchor="middle"
            fontWeight="600"
          >
            B/E: ${breakEven.toFixed(2)}
          </text>
        </>
      )}
      
      {/* X-axis labels (stock prices) */}
      {priceLabels.map((price, i) => (
        <text 
          key={`x-${i}`} 
          x={xScale(price)} 
          y={height - 10} 
          fill="#64748b" 
          fontSize="9" 
          textAnchor="middle"
        >
          ${price.toFixed(0)}
        </text>
      ))}
      
      {/* Max profit/loss annotations */}
      <text x={width - padding.right + 5} y={yScale(maxPL)} fill="#10b981" fontSize="9" fontWeight="600">
        Max: ${maxPL.toFixed(0)}
      </text>
      <text x={width - padding.right + 5} y={yScale(minPL)} fill="#ef4444" fontSize="9" fontWeight="600">
        Max: ${minPL.toFixed(0)}
      </text>
      
      {/* Legend */}
      <text x={padding.left} y={height - 5} fill="#94a3b8" fontSize="9">
        Stock Price at Expiration →
      </text>
      <text x={padding.left - 55} y={padding.top + chartHeight / 2} fill="#94a3b8" fontSize="9" transform={`rotate(-90, ${padding.left - 55}, ${padding.top + chartHeight / 2})`}>
        Profit/Loss ($)
      </text>
      
      {/* Educational annotations */}
      <g>
        {/* Max Loss annotation */}
        <rect x={width - 200} y={5} width={190} height={60} rx={6} fill="rgba(15, 23, 42, 0.9)" stroke="#334155" />
        <text x={width - 190} y={22} fill="#94a3b8" fontSize="10" fontWeight="600">📊 P&L Chart Guide</text>
        <text x={width - 190} y={38} fill="#ef4444" fontSize="9">● Max Loss: ${Math.abs(minPL).toFixed(0)} (premium paid)</text>
        <text x={width - 190} y={52} fill="#10b981" fontSize="9">● Profit Zone: Above $0 line</text>
      </g>
    </svg>
  );
};

// Helper Components (same as before, but with pagination)
const StatCard = ({ label, value, icon, color = 'white' }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '0.5rem',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
      {icon}
      <span style={{ fontSize: '0.75rem' }}>{label}</span>
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color }}>{value}</div>
  </div>
);

const TabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    style={{
      padding: '0.75rem 1.5rem',
      background: active ? '#8b5cf6' : 'transparent',
      border: 'none',
      borderRadius: '0.5rem 0.5rem 0 0',
      color: active ? 'white' : '#94a3b8',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
    }}
    onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.background = 'transparent';
    }}
  >
    {icon}
    {label}
  </button>
);

const OptionsChainTable = ({ calls, puts, allCalls, allPuts, underlyingPrice, selectedType, setSelectedType, currentPage, setCurrentPage, itemsPerPage, getTotalPages, onSelectOption, selectedOption }) => {
  const callsTotalPages = getTotalPages(allCalls);
  const putsTotalPages = getTotalPages(allPuts);

  return (
    <div>
      {/* Type Selector */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }}>
        {['all', 'calls', 'puts'].map(type => (
          <button
            key={type}
            onClick={() => {
              setSelectedType(type);
              setCurrentPage(1);
            }}
            style={{
              padding: '0.5rem 1rem',
              background: selectedType === type ? '#8b5cf6' : '#334155',
              border: 'none',
              borderRadius: '0.375rem',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              textTransform: 'capitalize'
            }}
          >
            {type}
          </button>
        ))}
        
        <div style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: '0.875rem' }}>
          {selectedType === 'all' && `Showing ${calls.length + puts.length} of ${allCalls.length + allPuts.length} contracts`}
          {selectedType === 'calls' && `Showing ${calls.length} of ${allCalls.length} calls`}
          {selectedType === 'puts' && `Showing ${puts.length} of ${allPuts.length} puts`}
        </div>
      </div>

      {(selectedType === 'all' || selectedType === 'calls') && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#10b981', fontWeight: '600' }}>
            <TrendingUp size={20} style={{ display: 'inline', marginRight: '0.5rem' }} />
            CALLS ({allCalls.length} total)
          </h3>
          <OptionsTable options={calls} type="call" underlyingPrice={underlyingPrice} onSelectOption={onSelectOption} selectedOption={selectedOption} />
          {selectedType === 'calls' && (
            <Pagination 
              currentPage={currentPage}
              totalPages={callsTotalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      {(selectedType === 'all' || selectedType === 'puts') && (
        <div>
          <h3 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#ef4444', fontWeight: '600' }}>
            <TrendingDown size={20} style={{ display: 'inline', marginRight: '0.5rem' }} />
            PUTS ({allPuts.length} total)
          </h3>
          <OptionsTable options={puts} type="put" underlyingPrice={underlyingPrice} onSelectOption={onSelectOption} selectedOption={selectedOption} />
          {selectedType === 'puts' && (
            <Pagination 
              currentPage={currentPage}
              totalPages={putsTotalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      {selectedType === 'all' && (
        <Pagination 
          currentPage={currentPage}
          totalPages={Math.max(callsTotalPages, putsTotalPages)}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

// Continue with OptionsTable, UnusualActivityTab, GreeksTab components...
// (I'll add these in the next part due to length)

const OptionsTable = ({ options, type, underlyingPrice, onSelectOption, selectedOption }) => {
  if (!options || options.length === 0) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        color: '#94a3b8',
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '0.75rem'
      }}>
        No options match the current filters
      </div>
    );
  }

  // Safe number formatter
  const safeFixed = (val, decimals = 2) => {
    if (val === null || val === undefined || isNaN(val)) return '--';
    return Number(val).toFixed(decimals);
  };
  
  const safeLocale = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '--';
    return Number(val).toLocaleString();
  };
  
  // Check if option is selected
  const isSelected = (opt) => {
    if (!selectedOption || !opt) return false;
    return selectedOption.strike === opt.strike && 
           selectedOption.type === opt.type && 
           selectedOption.expiration === opt.expiration;
  };

  return (
    <MobileTable>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
        <thead>
          <tr style={{ background: '#1e293b', borderBottom: '2px solid #334155' }}>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.strike}>Strike</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.premium}>Last (Premium)</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.bid}>Bid</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.ask}>Ask</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.change}>Change</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.volume}>Volume</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.oi}>OI</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.iv}>IV</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.delta}>Delta</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.gamma}>Gamma</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.theta}>Theta</Tooltip></th>
            <th style={headerStyle}><Tooltip content={COLUMN_TOOLTIPS.vega}>Vega</Tooltip></th>
          </tr>
        </thead>
        <tbody>
          {options.map((option, idx) => {
            if (!option) return null;
            
            const strike = option.strike || 0;
            const lastPrice = option.lastPrice || option.last || option.mark || 0;
            const bid = option.bid || 0;
            const ask = option.ask || 0;
            const change = option.change || 0;
            const changePercent = option.changePercent || 0;
            const volume = option.volume || 0;
            const openInterest = option.openInterest || option.oi || 0;
            const iv = option.impliedVolatility || option.iv || 0;
            const delta = option.delta || 0;
            const gamma = option.gamma || 0;
            const theta = option.theta || 0;
            const vega = option.vega || 0;
            const inTheMoney = option.inTheMoney || false;
            const selected = isSelected(option);
            
            return (
              <tr 
                key={idx}
                onClick={() => onSelectOption && onSelectOption({ ...option, type })}
                style={{ 
                  borderBottom: '1px solid #334155',
                  background: selected 
                    ? 'rgba(139, 92, 246, 0.3)' 
                    : inTheMoney 
                      ? (type === 'call' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)') 
                      : 'transparent',
                  cursor: onSelectOption ? 'pointer' : 'default',
                  transition: 'background 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  if (!selected) {
                    e.currentTarget.style.background = 'rgba(139, 92, 246, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selected) {
                    e.currentTarget.style.background = inTheMoney 
                      ? (type === 'call' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)') 
                      : 'transparent';
                  }
                }}
              >
                <td style={cellStyle}>
                  <div style={{ fontWeight: 'bold', color: selected ? '#a78bfa' : (inTheMoney ? '#60a5fa' : '#cbd5e1') }}>
                    ${safeFixed(strike)}
                  </div>
                  {underlyingPrice && Math.abs(strike - underlyingPrice) / underlyingPrice < 0.02 && (
                    <div style={{ fontSize: '0.65rem', color: '#f59e0b' }}>ATM</div>
                  )}
                </td>
                <td style={cellStyle}>${safeFixed(lastPrice)}</td>
                <td style={cellStyle}>${safeFixed(bid)}</td>
                <td style={cellStyle}>${safeFixed(ask)}</td>
                <td style={{...cellStyle, color: change >= 0 ? '#10b981' : '#ef4444'}}>
                  {change >= 0 ? '+' : ''}{safeFixed(changePercent)}%
                </td>
                <td style={cellStyle}>
                  {safeLocale(volume)}
                  {volume > 1000 && (
                    <Zap size={12} color="#f59e0b" style={{ marginLeft: '0.25rem' }} />
                  )}
                </td>
                <td style={cellStyle}>{safeLocale(openInterest)}</td>
                <td style={cellStyle}>
                  <div style={{ 
                    padding: '0.25rem 0.5rem',
                    background: getIVColor(iv / 100),
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    display: 'inline-block'
                  }}>
                    {safeFixed(iv, 1)}%
                  </div>
                </td>
                <td style={cellStyle}>{safeFixed(delta, 3)}</td>
                <td style={cellStyle}>{safeFixed(gamma, 4)}</td>
                <td style={cellStyle}>{safeFixed(theta, 4)}</td>
                <td style={cellStyle}>{safeFixed(vega, 4)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </MobileTable>
  );
};

const UnusualActivityTab = ({ unusualActivity, loading }) => {
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader size={32} className="spin" />
      </div>
    );
  }

  if (!unusualActivity || unusualActivity.length === 0) {
    return (
      <div style={{
        padding: '3rem',
        textAlign: 'center',
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '0.75rem'
      }}>
        <Zap size={48} color="#f59e0b" style={{ margin: '0 auto 1rem' }} />
        <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No Unusual Activity Detected</h3>
        <p style={{ color: '#94a3b8' }}>Check back later for options flow alerts</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        background: 'rgba(251, 191, 36, 0.1)',
        border: '1px solid rgba(251, 191, 36, 0.3)',
        borderRadius: '0.75rem',
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <p style={{ margin: 0, fontSize: '0.875rem', color: '#fbbf24' }}>
          <Zap size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
          <strong>Unusual Activity:</strong> Options with significantly higher than normal volume or unusual characteristics
        </p>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        {unusualActivity.map((option, idx) => {
          // Skip invalid options
          if (!option || typeof option !== 'object') return null;
          
          const optionType = option.type || 'unknown';
          const strike = option.strike || 0;
          const expiration = option.expiration || option.exp || 'N/A';
          const significance = option.significance || option.score || 0;
          const volume = option.volume || 0;
          const openInterest = option.openInterest || option.oi || 0;
          const volumeOIRatio = option.volumeOIRatio || (openInterest > 0 ? volume / openInterest : 0);
          const lastPrice = option.lastPrice || option.last || option.mark || 0;
          const iv = option.impliedVolatility || option.iv || 0;
          const reason = option.reason || 'High volume detected';
          
          return (
            <div
              key={idx}
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid #334155',
                borderRadius: '0.75rem',
                padding: '1.5rem'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: optionType === 'call' ? '#10b981' : '#ef4444' }}>
                    {optionType.toUpperCase()} ${strike}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    Exp: {expiration !== 'N/A' ? new Date(expiration).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                <div style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(251, 191, 36, 0.2)',
                  border: '1px solid rgba(251, 191, 36, 0.4)',
                  borderRadius: '0.5rem',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: '#fbbf24'
                }}>
                  <Zap size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                  Significance: {typeof significance === 'number' ? significance.toFixed(0) : significance}
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <MetricBox label="Volume" value={volume.toLocaleString()} />
                <MetricBox label="Open Interest" value={openInterest.toLocaleString()} />
                <MetricBox label="Volume/OI" value={typeof volumeOIRatio === 'number' ? volumeOIRatio.toFixed(2) : '0.00'} />
                <MetricBox label="Last Price" value={`$${typeof lastPrice === 'number' ? lastPrice.toFixed(2) : '0.00'}`} />
                <MetricBox label="IV" value={`${typeof iv === 'number' ? iv.toFixed(1) : '0.0'}%`} />
              </div>

              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                fontSize: '0.875rem',
                color: '#93c5fd'
              }}>
                <Info size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                {reason}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GreeksTab = ({ greeks, loading }) => {
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader size={32} className="spin" />
      </div>
    );
  }
  
  if (!greeks) {
    return (
      <div style={{
        padding: '3rem',
        textAlign: 'center',
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '0.75rem'
      }}>
        <Activity size={48} color="#8b5cf6" style={{ margin: '0 auto 1rem' }} />
        <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Greeks Data Unavailable</h3>
        <p style={{ color: '#94a3b8' }}>Greeks analysis requires options data with IV information</p>
      </div>
    );
  }

  // Safe accessors with defaults
  const totalDelta = typeof greeks.totalDelta === 'number' ? greeks.totalDelta : 0;
  const totalGamma = typeof greeks.totalGamma === 'number' ? greeks.totalGamma : 0;
  const totalTheta = typeof greeks.totalTheta === 'number' ? greeks.totalTheta : 0;
  const totalVega = typeof greeks.totalVega === 'number' ? greeks.totalVega : 0;
  const avgCallDelta = typeof greeks.avgCallDelta === 'number' ? greeks.avgCallDelta : 0;
  const avgPutDelta = typeof greeks.avgPutDelta === 'number' ? greeks.avgPutDelta : 0;

  // Format large numbers with K/M suffix
  const formatLargeNumber = (num) => {
    const absNum = Math.abs(num);
    if (absNum >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (absNum >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    } else if (absNum >= 1) {
      return num.toFixed(2);
    } else {
      return num.toFixed(4);
    }
  };

  return (
    <div>
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '2rem'
      }}>
        <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', fontWeight: '600' }}>
          Chain-wide Greeks Analysis
        </h3>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1.5rem'
        }}>
          <GreekCard
            label="Total Delta"
            value={formatLargeNumber(totalDelta)}
            description="Net directional exposure"
            color="#60a5fa"
          />
          <GreekCard
            label="Total Gamma"
            value={formatLargeNumber(totalGamma)}
            description="Delta sensitivity"
            color="#10b981"
          />
          <GreekCard
            label="Total Theta"
            value={formatLargeNumber(totalTheta)}
            description="Time decay per day"
            color="#ef4444"
          />
          <GreekCard
            label="Total Vega"
            value={formatLargeNumber(totalVega)}
            description="IV sensitivity"
            color="#8b5cf6"
          />
          <GreekCard
            label="Avg Call Delta"
            value={avgCallDelta.toFixed(3)}
            description="Average delta for calls"
            color="#10b981"
          />
          <GreekCard
            label="Avg Put Delta"
            value={avgPutDelta.toFixed(3)}
            description="Average delta for puts"
            color="#ef4444"
          />
        </div>

        <div style={{
          marginTop: '2rem',
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '0.5rem',
          padding: '1rem'
        }}>
          <h4 style={{ fontSize: '0.875rem', marginBottom: '0.5rem', color: '#93c5fd' }}>
            Understanding Greeks:
          </h4>
          <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.75rem', color: '#cbd5e1', lineHeight: '1.6' }}>
            <li><strong>Delta:</strong> Rate of change in option price per $1 move in underlying</li>
            <li><strong>Gamma:</strong> Rate of change in delta per $1 move in underlying</li>
            <li><strong>Theta:</strong> Rate of time decay per day</li>
            <li><strong>Vega:</strong> Sensitivity to 1% change in implied volatility</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const MetricBox = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>{label}</div>
    <div style={{ fontSize: '1.125rem', fontWeight: 'bold' }}>{value}</div>
  </div>
);

const GreekCard = ({ label, value, description, color }) => (
  <div style={{
    background: 'rgba(51, 65, 85, 0.5)',
    borderRadius: '0.5rem',
    padding: '1.5rem',
    border: '1px solid #475569'
  }}>
    <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>{label}</div>
    <div style={{ fontSize: '2rem', fontWeight: 'bold', color, marginBottom: '0.5rem' }}>{value}</div>
    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{description}</div>
  </div>
);

// Helper functions
function getDaysToExpiration(expirationDate) {
  // Handle demo data
  if (!expirationDate || 
      expirationDate === 'Demo Data' || 
      expirationDate.includes('2099') || 
      expirationDate.includes('99-99')) {
    return 'N/A';
  }
  
  const now = new Date();
  const exp = new Date(expirationDate);
  
  // Check if date is valid
  if (isNaN(exp.getTime())) {
    return 'N/A';
  }
  
  const diffTime = exp - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays : 0;
}

function getIVColor(iv) {
  if (iv > 1.0) return 'rgba(239, 68, 68, 0.3)';
  if (iv > 0.6) return 'rgba(251, 191, 36, 0.3)';
  if (iv > 0.3) return 'rgba(59, 130, 246, 0.3)';
  return 'rgba(100, 116, 139, 0.3)';
}

const headerStyle = {
  padding: '1rem 0.75rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const cellStyle = {
  padding: '0.75rem',
  fontSize: '0.875rem',
  color: '#cbd5e1'
};

export default OptionsChainExplorer;